/**
 * API layer for the server-mediated native auth.
 * Laravel calls Entra and returns the JWT; the SPA uses it as a bearer (as today).
 */
import axios from 'axios';

const BASE_ROUTE = 'api/native-auth';

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig';

/**
 * The SPA's selected UI language, sent to the backend as `X-App-Locale` so it
 * can hint Entra which language to send the OTP email in (experiment — browsers
 * forbid setting `Accept-Language` directly). Falls back to French.
 */
const appLocale = (): string => {
  try {
    const ls = localStorage.getItem(I18N_CONFIG_KEY);
    if (ls) {
      return (JSON.parse(ls) as {selectedLang?: string}).selectedLang || 'fr';
    }
  } catch {
    /* ignore malformed config */
  }
  return 'fr';
};

const localeHeaders = (): {headers: {'X-App-Locale': string}} => ({
  headers: {'X-App-Locale': appLocale()},
});

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string | null;
  expiresIn: number;
}

export interface MfaChallengeInfo {
  sentTo: string | null;
  channel: string | null;
  codeLength: number | null;
  methodId?: string | null;
}

interface CompletedResponse extends AuthTokens {
  status: 'completed';
}

interface MfaRequiredResponse {
  status: 'mfa_required';
  continuationToken: string;
  challenge: MfaChallengeInfo;
}

/**
 * JIT migration (AADSTS50219): the legacy password is valid but weak/expired, so
 * Entra requires a reset before sign-in. The SPA routes the user into the SSPR
 * flow (no continuation token — the reset flow starts fresh from the email).
 */
interface PasswordResetRequiredResponse {
  status: 'password_reset_required';
  /** Why the reset is required ('expired' | 'migration'), drives the notice text. */
  reason?: string;
}

export type NativeLoginResponse =
  | CompletedResponse
  | MfaRequiredResponse
  | PasswordResetRequiredResponse;

export const nativeLogin = async (
  email: string,
  password: string
): Promise<NativeLoginResponse> => {
  const {data} = await axios.post<NativeLoginResponse>(
    `${BASE_ROUTE}/login`,
    {email, password},
    localeHeaders()
  );
  return data;
};

export const nativeMfa = async (
  continuationToken: string,
  code: string
): Promise<CompletedResponse | PasswordResetRequiredResponse> => {
  const {data} = await axios.post<CompletedResponse | PasswordResetRequiredResponse>(
    `${BASE_ROUTE}/mfa`,
    {
      continuationToken,
      code,
    }
  );
  return data;
};

interface MfaResendResponse {
  status: 'mfa_required';
  continuationToken: string;
  challenge: MfaChallengeInfo;
}

export const mfaResend = async (
  continuationToken: string,
  methodId: string
): Promise<MfaResendResponse> => {
  const {data} = await axios.post<MfaResendResponse>(
    `${BASE_ROUTE}/mfa/resend`,
    {
      continuationToken,
      methodId,
    },
    localeHeaders()
  );
  return data;
};

export const nativeRefresh = async (refreshToken: string): Promise<CompletedResponse> => {
  const {data} = await axios.post<CompletedResponse>(`${BASE_ROUTE}/refresh`, {refreshToken});
  return data;
};

// --- Self-service password reset (SSPR) ---

interface ResetStartResponse {
  status: 'code_required';
  continuationToken: string;
  challenge: MfaChallengeInfo;
}

interface ResetVerifyResponse {
  status: 'password_required';
  continuationToken: string;
}

interface ResetCompletedResponse {
  status: 'completed';
}

export const resetStart = async (email: string): Promise<ResetStartResponse> => {
  const {data} = await axios.post<ResetStartResponse>(
    `${BASE_ROUTE}/reset/start`,
    {email},
    localeHeaders()
  );
  return data;
};

export const resetVerify = async (
  continuationToken: string,
  code: string
): Promise<ResetVerifyResponse> => {
  const {data} = await axios.post<ResetVerifyResponse>(`${BASE_ROUTE}/reset/verify`, {
    continuationToken,
    code,
  });
  return data;
};

export const resetSubmit = async (
  continuationToken: string,
  newPassword: string
): Promise<ResetCompletedResponse> => {
  const {data} = await axios.post<ResetCompletedResponse>(`${BASE_ROUTE}/reset/submit`, {
    continuationToken,
    newPassword,
  });
  return data;
};

export const resetResend = async (
  continuationToken: string,
  email: string
): Promise<ResetStartResponse> => {
  const {data} = await axios.post<ResetStartResponse>(
    `${BASE_ROUTE}/reset/resend`,
    {
      continuationToken,
      email,
    },
    localeHeaders()
  );
  return data;
};
